import type { ILocalizedDestinationMap } from '@/shared/api/types/destination-types'
import type { IPoiTranslation } from '@/shared/api/types/poi-types'
import { TRANSLATION_STATUS } from '@/shared/constants'

type LocalizedValue = Record<string, string>

export type LocalizedDataMap = Record<string, LocalizedValue>

export interface BaseTranslation {
  language: string
  name: string
  description: string
  audioContent?: string
  status?: string
  hasAudio?: boolean
  audioUrl?: string
}

function toBoolean(value: string | undefined): boolean | undefined {
  if (value === undefined) {
    return undefined
  }

  if (value === 'true') {
    return true
  }
  if (value === 'false') {
    return false
  }

  return undefined
}

export function normalizeLocalizedLanguageKey(language: string): string {
  return language
    .replace(/_/g, '-')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
}

export function normalizeLocalizedDataMap<
  T extends LocalizedDataMap | ILocalizedDestinationMap | null | undefined,
>(localizedData: T): T {
  if (!localizedData) {
    return localizedData
  }

  return Object.fromEntries(
    Object.entries(localizedData).map(([language, value]) => [
      normalizeLocalizedLanguageKey(language),
      value,
    ]),
  ) as T
}

export function localizedDataToBaseTranslations(
  localizedData?: LocalizedDataMap | null,
): BaseTranslation[] {
  const normalizedLocalizedData = normalizeLocalizedDataMap(localizedData)
  if (!normalizedLocalizedData) {
    return []
  }

  return Object.entries(normalizedLocalizedData).map(([language, value]) => ({
    language,
    name: value['name'] ?? '',
    description: value['description'] ?? '',
  }))
}

export function localizedDataToPoiTranslations(
  localizedData?: LocalizedDataMap | null,
): IPoiTranslation[] {
  const normalizedLocalizedData = normalizeLocalizedDataMap(localizedData)
  if (!normalizedLocalizedData) {
    return []
  }

  return Object.entries(normalizedLocalizedData).map(([language, value]) => {
    const statusValue = value['status']
    const parsedHasAudio = toBoolean(value['has_audio'])
    const audioUrl = value['audio_url'] ?? value['audio-url']
    return {
      language,
      name: value['name'] ?? '',
      description: value['description'] ?? '',
      audioContent: value['audio_content'] ?? '',
      status:
        statusValue === TRANSLATION_STATUS.READY || statusValue === TRANSLATION_STATUS.NEEDS_REVIEW
          ? statusValue
          : TRANSLATION_STATUS.DRAFT,
      hasAudio: parsedHasAudio ?? Boolean(audioUrl),
      ...(audioUrl ? { audioUrl } : {}),
    }
  })
}

export function baseTranslationsToLocalizedData<T extends BaseTranslation>(
  translations: T[],
  options?: { includeMeta?: boolean },
): LocalizedDataMap {
  return translations.reduce<LocalizedDataMap>((acc, translation) => {
    const language = translation.language.trim()
    if (!language) {
      return acc
    }

    acc[language] = {
      name: translation.name,
      description: translation.description,
      ...(translation.audioContent !== undefined
        ? { audio_content: translation.audioContent }
        : {}),
    }

    if (options?.includeMeta && translation.audioUrl) {
      acc[language]['audio_url'] = translation.audioUrl
    }

    return acc
  }, {})
}
